import StudioApiService from "@/services/StudioApiService";
import { createAsyncThunk, createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AsyncStatus, GameSystems, IArmyData } from "../types";
import { showErrorDialog } from "./appSlice";

export interface ArmyState {
  loadedArmyBooks: IArmyData[];
  loadedArmyBooksStatus: AsyncStatus;
  loadingBookIds: string[];
}

const initialState: ArmyState = {
  loadedArmyBooks: [],
  loadedArmyBooksStatus: AsyncStatus.idle,
  loadingBookIds: []
};

export const getArmyBookData = createAsyncThunk(
  "army/getArmyBookData",
  async (
    payload: {
      armyUids: string[];
      gameSystem: GameSystems;
      reset: boolean;
      options?: { simpleMode?: boolean; includeVehicles?: boolean; swarmMode?: boolean };
    },
    { dispatch }
  ) => {
    try {
      const promises = payload.armyUids.map((x) =>
        StudioApiService.getArmyBookData(x, payload.gameSystem, payload.options || {})
      );

      const data = await Promise.all(promises);

      return { results: data, reset: payload.reset };
    } catch {
      dispatch(
        showErrorDialog({
          title: "Error Loading Army Book",
          message:
            "There was an issue loading the army book. If you are trying to view a private book, ensure that you are logged in."
        })
      );
      throw "Error loading army book";
    }
  }
);

const armySlice = createSlice({
  name: "army",
  initialState: initialState,
  reducers: {
    resetLoadedBooks(state) {
      state.loadedArmyBooks = [];
    },
    addArmyBook(state, action: PayloadAction<IArmyData>) {
      const book = action.payload;
      if (!state.loadedArmyBooks.some((x) => x.uid === book.uid)) {
        state.loadedArmyBooks.push(book);
      }
    },

    unloadArmyBook(state, action: PayloadAction<string>) {
      const uid = action.payload;
      const index = state.loadedArmyBooks.findIndex((book) => book.uid === uid);
      state.loadedArmyBooks.splice(index, 1);
    }
  },
  selectors: {
    selectLoadedArmyBooks(state) {
      return state.loadedArmyBooks;
    }
  },
  extraReducers(builder) {
    builder
      .addCase(getArmyBookData.pending, (state, action) => {
        return {
          ...state,
          loadingBookIds: action.meta.arg.armyUids,
          loadedArmyBooksStatus: AsyncStatus.loading
        };
      })
      .addCase(
        getArmyBookData.fulfilled,
        (state, action: PayloadAction<{ results: IArmyData[]; reset: boolean }>) => {
          const { results, reset } = action.payload;

          state.loadingBookIds = [];
          state.loadedArmyBooksStatus = AsyncStatus.succeeded;

          if (reset) {
            state.loadedArmyBooks = results;
            return;
          }

          const loadedIds = results.map((x) => x.uid);
          state.loadedArmyBooks = state.loadedArmyBooks
            .filter((x) => !loadedIds.includes(x.uid))
            .concat(results);
        }
      );
  }
});

// Action creators are generated for each case reducer function
export const { resetLoadedBooks, unloadArmyBook, addArmyBook } = armySlice.actions;

export const { selectLoadedArmyBooks } = armySlice.selectors;

export const selectArmyBookRules = createSelector([selectLoadedArmyBooks], (books) => {
  return books.flatMap((x) => x.specialRules.map((r) => ({ ...r, gameSystem: x.gameSystemId })));
});

export const selectSpellRulesInBooks = createSelector([selectLoadedArmyBooks], (books) => {
  return books.flatMap((x) => x.usedRulesInSpells || []);
});

export const selectArmyBookUnits = createSelector([selectLoadedArmyBooks], (books) => {
  return books.flatMap((x) => x.units);
});

export default armySlice.reducer;
